import * as React from 'react'
import { Route, Routes, redirect } from 'react-router-dom'

import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import {
  AtsCandidateHeaderDocument,
  AtsContextDocument,
  AtsJobApplicationBasesHeaderDocument,
  AtsJobPostingHeaderDocument,
  AtsJobPostingSettingsHeaderDocument,
  AtsJobsTabsDocument,
  AtsTabsDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { ModuleMeta } from 'modules/core/lib/navigation/types'
import { type NavigationRoutesFn, buildModuleRoute } from 'modules/core/lib/react-router'

import CandidateCvPage from 'modules/ats/pages/Candidates/ShowCandidate/CandidateCV'
import { CandidateHeader } from 'modules/ats/pages/Candidates/ShowCandidate/CandidateDetailPage/CandidateHeader'
import { JobApplicationComponentHeader } from 'modules/ats/pages/JobApplication/JobApplicationHeader/ComponentHeader'
import { JobPostingMarketplace } from 'modules/ats/pages/JobPostingMarketplace'
import { JobPostingPremiumPortals } from 'modules/ats/pages/JobPostingPremiumPortals'
import { JobPostingSettingsHeader } from 'modules/ats/pages/JobPostingSettings/Header/JobPostingSettingsHeader'
import { JobPostingStandardPortals } from 'modules/ats/pages/JobPostingStandardPortals'
import { JobPostingHeader } from 'modules/ats/pages/JobsPage/JobDetails/JobPosting/Header/JobPostingHeader'
import { WHATSAPP_INTEGRATION_ID } from 'modules/marketplace/lib/integrationIds'

const RootPage = React.lazy(() => import('modules/ats/pages/RootPage'))
const CandidateNotesPage = React.lazy(() => import('modules/ats/pages/CandidateNotesPage'))
const CandidateApplications = React.lazy(
  () => import('modules/ats/pages/Candidates/ShowCandidate/CandidateApplications')
)
const CandidateDetailPage = React.lazy(
  () => import('modules/ats/pages/Candidates/ShowCandidate/CandidateDetailPage')
)
const CandidateFiles = React.lazy(
  () => import('modules/ats/pages/Candidates/ShowCandidate/CandidateFiles')
)
const CandidatesPage = React.lazy(() => import('modules/ats/pages/CandidatesPage'))
const ImportCandidatesPage = React.lazy(() => import('modules/ats/pages/ImportCandidatesPage'))
const InsightsPage = React.lazy(() => import('modules/ats/pages/InsightsPage'))
const JobApplicationConvertToEmployeePage = React.lazy(
  () => import('modules/ats/pages/JobApplicationConvertToEmployeePage')
)
const JobApplicationCreateFromCvPage = React.lazy(
  () => import('modules/ats/pages/JobApplicationCreateFromCvPage')
)
const JobApplicationOverviewPage = React.lazy(
  () => import('modules/ats/pages/JobApplicationOverview')
)
const JobApplicationFilesPage = React.lazy(() => import('modules/ats/pages/JobApplicationFiles'))
const JobApplicationOtherInfoPage = React.lazy(
  () => import('modules/ats/pages/JobApplicationOtherInfo')
)
const JobApplicationDetailMessagesPage = React.lazy(
  () => import('modules/ats/pages/JobApplicationDetailMessagesPage')
)
const JobApplicationNotesPage = React.lazy(() => import('modules/ats/pages/JobApplicationNotes'))
const JobApplicationOfferLetterPage = React.lazy(
  () => import('modules/ats/pages/JobApplicationOfferLetter')
)
const JobApplicationPage = React.lazy(() => import('modules/ats/pages/JobApplication'))
const JobApplicationsAddCandidatePage = React.lazy(
  () => import('modules/ats/pages/JobApplicationsAddCandidatePage')
)
const JobApplicationsAddEmployeeCandidatePage = React.lazy(
  () => import('modules/ats/pages/JobApplicationsAddEmployeeCandidatePage')
)
const JobApplicationsPage = React.lazy(() => import('modules/ats/pages/JobApplicationsPage'))
const JobPostingPromotion = React.lazy(() => import('modules/ats/pages/JobPostingPromotion'))

const JobPostingSettings = React.lazy(() => import('modules/ats/pages/JobPostingSettings'))

const JobRequisitionDetailPage = React.lazy(
  () => import('modules/ats/pages/JobRequisitionDetailPage')
)
const JobsCampaignsPage = React.lazy(() => import('modules/ats/pages/JobsCampaignsPage'))
const JobsPage = React.lazy(() => import('modules/ats/pages/JobsPage'))
const JobDetails = React.lazy(() => import('modules/ats/pages/JobsPage/JobDetails'))

const JobsRequisitionsPage = React.lazy(() => import('modules/ats/pages/JobsRequisitionsPage'))
const MessageInboxConversationViewPage = React.lazy(
  () => import('modules/ats/pages/MessageInboxConversationViewPage')
)
const MessagesInboxPage = React.lazy(() => import('modules/ats/pages/MessagesInboxPage'))
const Overview = React.lazy(() => import('modules/ats/pages/MessagesInboxPage/Overview'))
const NewCandidateFromEmployeePage = React.lazy(
  () => import('modules/ats/pages/NewCandidateFromEmployeePage')
)
const NewCandidatePage = React.lazy(() => import('modules/ats/pages/NewCandidatePage'))
const NewJobPostingModalPage = React.lazy(() => import('modules/ats/pages/NewJobModalPage'))
const NewJobRequisitionModalPage = React.lazy(
  () => import('modules/ats/pages/NewJobRequisitionModalPage')
)
const OpenPositionsPage = React.lazy(() => import('modules/ats/pages/OpenPositionsPage'))
const UniversalImporterCandidatesPage = React.lazy(
  () => import('modules/ats/pages/UniversalImporterCandidatesPage')
)
const UniversalImporterJobPostingsPage = React.lazy(
  () => import('modules/ats/pages/UniversalImporterJobPostingsPage')
)
const PurchaseRequestsPage = React.lazy(() => import('modules/ats/pages/PurchaseRequestsPage'))
const PurchaseRequestDetailPage = React.lazy(
  () => import('modules/ats/pages/PurchaseRequestDetailPage')
)

const cache = new Map()

export const atsDataRoutes: NavigationRoutesFn = (navigation) => {
  return [
    buildModuleRoute({
      path: navigation.ats.path,
      element: <RootPage />,
      handle: {
        module: 'ats',
        moduleName: () => i18n.t('sidebar.recruitment'),
        meta: ({ watchQuery }) => {
          return watchQuery({
            query: AtsTabsDocument,
            variables: {},
          }).map(({ data }): ModuleMeta => {
            return {
              title: i18n.t('sidebar.recruitment'),
              settings: navigation.settings.ats.fullPath(),
              subNavigation: [
                {
                  title: i18n.t('ats.routes.jobs'),
                  href: navigation.ats.jobs.fullPath(),
                  isVisible: data?.ats._policies.sidebarSeeRecruitmentJobsSubLevelItem.can,
                },
                {
                  title: i18n.t('ats.routes.candidates'),
                  href: navigation.ats.candidates.fullPath(),
                  isVisible: data?.ats._policies.sidebarSeeRecruitmentCandidatesSubLevelItem.can,
                },
                {
                  title: i18n.t('ats.routes.messages'),
                  href: navigation.ats.messages.fullPath(),
                  isVisible: data?.ats._policies.sidebarSeeRecruitmentInboxSubLevelItem.can,
                },
                {
                  title: i18n.t('ats.routes.open_positions'),
                  href: navigation.ats.openPositions.fullPath(),
                  isVisible: data?.ats._policies.sidebarSeeRecruitmentOpenPositionsSubLevelItem.can,
                },
                {
                  title: i18n.t('ats.routes.insights'),
                  href: navigation.ats.insights.fullPath(),
                  isVisible: data?.ats._policies.sidebarSeeRecruitmentInsightsSubLevelItem.can,
                },
                {
                  title: i18n.t('ats.routes.purchase_requests'),
                  href: navigation.ats.purchaseRequests.fullPath(),
                  isVisible:
                    data.ats._policies.sidebarSeeRecruitmentPurchaseRequestsSubLevelItem.can,
                },
              ],
            }
          })
        },
      },
      children: [
        // -- Jobs
        {
          path: navigation.ats.jobs.path,
          handle: {
            meta: ({ watchQuery, session }) => {
              const cacheKey = `job-${session.company.id}`

              if (cache.has(cacheKey)) {
                return cache.get(cacheKey)
              }

              return watchQuery({
                query: AtsJobsTabsDocument,
                variables: {
                  companyId: session.company.id,
                },
              }).map(({ data }) => {
                const isRequisitionsEnabled =
                  data?.ats._policies.requisitionsUse.can &&
                  data?.companies.requisitionsEnabled?.available &&
                  data?.ats.company?.requisitionsEnabled

                const resultData = {
                  title: i18n.t('ats.routes.jobs'),
                  subNavigation: [
                    {
                      title: i18n.t('ats.routes.job_openings'),
                      href: navigation.ats.jobs.fullPath(),
                      index: true,
                    },
                    {
                      title: i18n.t('ats.routes.campaigns'),
                      href: navigation.ats.jobs.campaigns.fullPath(),
                      isVisible: Boolean(data?.companies.vonqEnabled?.available),
                    },
                    {
                      title: i18n.t('ats.routes.requisitions'),
                      href: navigation.ats.jobs.requisitions.fullPath(),
                      isVisible: Boolean(isRequisitionsEnabled),
                    },
                  ],
                }

                cache.set(cacheKey, resultData)
                return resultData
              })
            },
          },
          children: [
            {
              children: [
                { index: true },
                {
                  path: navigation.ats.jobs.new.path,
                  element: <NewJobPostingModalPage />,
                },
              ],
              element: <JobsPage />,
            },
            {
              path: navigation.ats.jobs.campaigns.path,
              element: <JobsCampaignsPage />,
            },
            {
              path: navigation.ats.jobs.requisitions.path,

              children: [
                {
                  path: navigation.ats.jobs.requisitions.new.path,
                  element: <NewJobRequisitionModalPage />,
                },
                {
                  path: navigation.ats.jobs.requisitions.requisitionDetail.path,
                  element: <JobRequisitionDetailPage />,
                },
              ],
              element: <JobsRequisitionsPage />,
            },
            // -- Job detail
            // TODO: Move each page to its own folder. Currently, each page from the Job section is a subfolder under JobPage.
            {
              path: navigation.ats.jobs.jobsId.path,

              handle: {
                resource: true,
                meta: ({ watchQuery, params, session }) => {
                  const cacheKey = `jobPosting-${params.jobId}-${session.company.id}`

                  if (cache.has(cacheKey)) {
                    return cache.get(cacheKey)
                  }

                  return watchQuery({
                    query: AtsJobPostingHeaderDocument,
                    variables: {
                      id: Number(params.jobId),
                      companyId: session.company.id,
                      accessId: session.access.id,
                    },
                  }).map(({ data }) => {
                    const atsPolicies = data.ats._policies
                    const jobPosting = data.ats.jobPosting
                      ? {
                          ...data.ats.jobPosting,
                          currentHiringRole: data.ats.jobPosting.currentHiringRole?.[0] ?? null,
                        }
                      : null

                    const isCompanyPageInstalled =
                      data?.features.enabledFeatures.nodes.find(
                        (node) => node.name === 'ats_careers_page'
                      )?.enabled ?? false
                    const isRequisitionsEnabled =
                      (data?.companies.requisitionsEnabled?.available &&
                        data?.ats.company?.requisitionsEnabled) ??
                      false
                    if (!jobPosting) {
                      return { title: '' }
                    }

                    const resultData = {
                      title: jobPosting.title,
                      headerComponent: (
                        <JobPostingHeader
                          jobData={jobPosting}
                          isCompanyPageInstalled={isCompanyPageInstalled}
                          policies={atsPolicies}
                          isRequisitionsEnabled={isRequisitionsEnabled}
                        />
                      ),
                    }

                    cache.set(cacheKey, resultData)
                    return resultData
                  })
                },
              },

              children: [
                {
                  path: navigation.ats.jobs.jobsId.settings.path,
                  handle: {
                    resource: true,
                    meta: ({ watchQuery, params, session }) => {
                      return watchQuery({
                        query: AtsJobPostingSettingsHeaderDocument,
                        variables: {
                          id: Number(params.jobId),
                          companyId: session.company.id,
                          accessId: session.access.id,
                        },
                      }).map(({ data }) => {
                        const jobPosting = data.ats.jobPosting
                        const atsPolicies = data.ats._policies
                        const isCompanyPageInstalled =
                          data?.features.enabledFeatures.nodes.find(
                            (node) => node.name === 'ats_careers_page'
                          )?.enabled ?? false
                        if (!jobPosting) {
                          return { title: i18n.t('ats.job_posting_settings.title') }
                        }

                        return {
                          title: i18n.t('ats.job_posting_settings.title'),
                          headerComponent: (
                            <JobPostingSettingsHeader
                              jobData={jobPosting}
                              isCompanyPageInstalled={isCompanyPageInstalled}
                              policies={atsPolicies}
                            />
                          ),
                        }
                      })
                    },
                  },
                  element: <JobPostingSettings />,
                },
                {
                  index: true,
                  loader: ({ params }) => {
                    return redirect(
                      navigation.ats.jobs.jobsId.applications.fullPath({
                        jobId: Number(params.jobId),
                      })
                    )
                  },
                },
                {
                  path: navigation.ats.jobs.jobsId.applications.path,

                  children: [
                    {
                      index: true,
                      element: <JobApplicationsPage />,
                    },
                    {
                      path: navigation.ats.jobs.jobsId.applications.new.path,
                      element: <JobApplicationsAddCandidatePage />,
                    },
                    {
                      path: navigation.ats.jobs.jobsId.applications.newFromCv.path,
                      element: <JobApplicationCreateFromCvPage />,
                    },
                    {
                      path: navigation.ats.jobs.jobsId.applications.newEmployeeCandidate.path,
                      element: <JobApplicationsAddEmployeeCandidatePage />,
                    },
                    // Application
                    {
                      path: navigation.ats.jobs.jobsId.applications.applicationDetail.path,
                      handle: {
                        resource: true,
                        meta: ({ watchQuery, params, session }) => {
                          if (!params.application_id) {
                            return { title: '' }
                          }

                          // Get Hiring Role by accessId
                          return watchQuery({
                            query: AtsJobApplicationBasesHeaderDocument,
                            variables: {
                              id: Number(params.application_id),
                              accessId: session.access.id,
                            },
                            fetchPolicy: 'network-only',
                          }).map(({ data }) => {
                            const canManageJobPostings =
                              data?.ats._policies.manageJobPostings.can ?? false
                            const hiringRole =
                              data.ats.applicationsConnection.nodes[0]?.jobPosting
                                .hiringmanagersConnection.nodes[0]?.hiringRole ?? 'editor'
                            const isReviewerRole =
                              hiringRole === 'reviewer' && !canManageJobPostings
                            const application = data.ats.applicationsConnection.nodes[0]

                            if (!application) {
                              return {
                                title: '',
                              }
                            }

                            const subNavigationParams = {
                              jobId: application.jobPosting.id,
                              application_id: application.id,
                            }

                            const prevApplication = {
                              id: data.ats.prevApplication.nodes[0]?.id ?? null,
                              fullName: data.ats.prevApplication.nodes[0]?.candidate.fullName ?? '',
                            }

                            const nextApplication = {
                              id: data.ats.nextApplication.nodes[0]?.id ?? null,
                              fullName: data.ats.nextApplication.nodes[0]?.candidate.fullName ?? '',
                            }

                            return {
                              title: application.fullName,
                              headerComponent: (
                                <JobApplicationComponentHeader
                                  isReviewerRole={isReviewerRole}
                                  applicationId={Number(params.application_id)}
                                  hiringRole={hiringRole}
                                />
                              ),
                              subNavigation: isReviewerRole
                                ? undefined
                                : [
                                    {
                                      title: i18n.t('ats.application_details.tabs.applications'),
                                      href: navigation.ats.jobs.jobsId.applications.applicationDetail.index.fullPath(
                                        subNavigationParams
                                      ),
                                      index: true,
                                    },
                                    {
                                      title: i18n.t('ats.application_details.tabs.notes'),
                                      href: navigation.ats.jobs.jobsId.applications.applicationDetail.notes.fullPath(
                                        subNavigationParams
                                      ),
                                    },
                                    {
                                      title: i18n.t('ats.application_details.tabs.files'),
                                      href: navigation.ats.jobs.jobsId.applications.applicationDetail.files.fullPath(
                                        subNavigationParams
                                      ),
                                    },
                                    {
                                      title: i18n.t('ats.application_details.tabs.messages'),
                                      href: navigation.ats.jobs.jobsId.applications.applicationDetail.messages.fullPath(
                                        subNavigationParams
                                      ),
                                    },
                                    {
                                      title: i18n.t('ats.application_details.tabs.other_info'),
                                      href: navigation.ats.jobs.jobsId.applications.applicationDetail.otherInfo.fullPath(
                                        subNavigationParams
                                      ),
                                    },
                                  ],
                              siblingsNavigation: {
                                previous: prevApplication.id
                                  ? {
                                      title: prevApplication.fullName,
                                      url: navigation.ats.jobs.jobsId.applications.applicationDetail.fullPath(
                                        {
                                          jobId: application.jobPosting.id,
                                          application_id: prevApplication.id,
                                        }
                                      ),
                                    }
                                  : undefined,
                                next: nextApplication.id
                                  ? {
                                      title: nextApplication.fullName,
                                      url: navigation.ats.jobs.jobsId.applications.applicationDetail.fullPath(
                                        {
                                          jobId: application.jobPosting.id,
                                          application_id: nextApplication.id,
                                        }
                                      ),
                                    }
                                  : undefined,
                              },
                            }
                          })
                        },
                      },
                      element: <JobApplicationPage />,
                      children: [
                        {
                          path: navigation.ats.jobs.jobsId.applications.applicationDetail.index
                            .path,

                          children: [
                            {
                              path: navigation.ats.jobs.jobsId.applications.applicationDetail.index
                                .convertToEmployee.path,
                              element: <JobApplicationConvertToEmployeePage />,
                            },
                          ],
                          element: <JobApplicationOverviewPage />,
                        },
                        {
                          path: navigation.ats.jobs.jobsId.applications.applicationDetail.messages
                            .path,

                          children: [
                            {
                              path: navigation.ats.jobs.jobsId.applications.applicationDetail
                                .messages.convertToEmployee.path,
                              element: <JobApplicationConvertToEmployeePage />,
                            },
                          ],
                          element: <JobApplicationDetailMessagesPage />,
                        },
                        {
                          path: navigation.ats.jobs.jobsId.applications.applicationDetail.notes
                            .path,

                          children: [
                            {
                              path: navigation.ats.jobs.jobsId.applications.applicationDetail.notes
                                .convertToEmployee.path,
                              element: <JobApplicationConvertToEmployeePage />,
                            },
                          ],
                          element: <JobApplicationNotesPage />,
                        },
                        {
                          // TODO: delete this after 11/03/2024
                          path: navigation.ats.jobs.jobsId.applications.applicationDetail.feedback
                            .path,
                          loader: ({ params }) => {
                            return redirect(
                              navigation.ats.jobs.jobsId.applications.applicationDetail.notes.fullPath(
                                {
                                  jobId: Number(params.jobId),
                                  application_id: Number(params.application_id),
                                }
                              )
                            )
                          },
                        },
                        {
                          path: navigation.ats.jobs.jobsId.applications.applicationDetail.files
                            .path,

                          children: [
                            {
                              path: navigation.ats.jobs.jobsId.applications.applicationDetail.files
                                .convertToEmployee.path,
                              element: <JobApplicationConvertToEmployeePage />,
                            },
                          ],
                          element: <JobApplicationFilesPage />,
                        },
                        {
                          path: navigation.ats.jobs.jobsId.applications.applicationDetail.otherInfo
                            .path,
                          element: <JobApplicationOtherInfoPage />,
                        },
                        {
                          path: navigation.ats.jobs.jobsId.applications.applicationDetail
                            .offerLetter.path,
                          element: <JobApplicationOfferLetterPage />,
                        },
                      ],
                    },
                  ],
                },
                {
                  path: navigation.ats.jobs.jobsId.promotion.path,

                  handle: {
                    meta: () => ({
                      title: i18n.t('ats.job_details.tab_headers.promotion'),
                    }),
                  },
                  element: <JobPostingPromotion />,
                },
                {
                  path: navigation.ats.jobs.jobsId.standardPortals.path,
                  handle: {
                    meta: ({ watchQuery, params, navigate, session }) => {
                      return watchQuery({
                        query: AtsContextDocument,
                        variables: { companyId: session.company.id },
                      }).map(({ data }) => {
                        const isJoinEnabled =
                          data.ats.companiesConnection.nodes[0]?.joinEnabled ?? false

                        if (!isJoinEnabled) {
                          navigate(
                            navigation.ats.jobs.jobsId.applications.fullPath({
                              jobId: Number(params.jobId),
                            })
                          )
                        }

                        return {
                          title: i18n.t('ats.job_details.tab_headers.standard_portals'),
                        }
                      })
                    },
                  },
                  element: <JobPostingStandardPortals />,
                },
                {
                  path: navigation.ats.jobs.jobsId.premiumPortals.path,
                  handle: {
                    meta: ({ watchQuery, params, navigate, session }) => {
                      return watchQuery({
                        query: AtsContextDocument,
                        variables: { companyId: session.company.id },
                      }).map(({ data }) => {
                        const isJoinEnabled =
                          data.ats.companiesConnection.nodes[0]?.joinEnabled ?? false

                        const hasPremiumPortals =
                          data?.features.enabledFeatures.nodes.find(
                            (node) => node.name === 'dev_ats_join_premium_portals'
                          )?.enabled ?? false

                        if (!isJoinEnabled || !hasPremiumPortals) {
                          navigate(
                            navigation.ats.jobs.jobsId.applications.fullPath({
                              jobId: Number(params.jobId),
                            })
                          )
                        }

                        return {
                          title: i18n.t('ats.job_details.tab_headers.premium_portals'),
                        }
                      })
                    },
                  },
                  element: <JobPostingPremiumPortals />,
                },
                {
                  path: navigation.ats.jobs.jobsId.marketplace.path,
                  handle: {
                    meta: ({ watchQuery, params, navigate, session }) => {
                      return watchQuery({
                        query: AtsContextDocument,
                        variables: { companyId: session.company.id },
                      }).map(({ data }) => {
                        const isJoinEnabled =
                          data.ats.companiesConnection.nodes[0]?.joinEnabled ?? false

                        const hasPremiumPortals =
                          data?.features.enabledFeatures.nodes.find(
                            (node) => node.name === 'dev_ats_join_premium_portals'
                          )?.enabled ?? false

                        if (!isJoinEnabled || !hasPremiumPortals) {
                          navigate(
                            navigation.ats.jobs.jobsId.applications.fullPath({
                              jobId: Number(params.jobId),
                            })
                          )
                        }

                        return {
                          title: i18n.t('ats.job_details.tab_headers.marketplace'),
                        }
                      })
                    },
                  },
                  element: <JobPostingMarketplace />,
                },
                {
                  path: navigation.ats.jobs.jobsId.candidates.path,
                  element: null,
                  loader: ({ params, request }) => {
                    // We're using replace because it covers all urls related to
                    // the candidates, even nested routes, for example:
                    // - /candidates
                    // - /candidates/38
                    // - /candidates/38/messages
                    // It also works with the "feedback" route, which is now "notes"

                    const index = request.url.indexOf('/recruitment')

                    if (index !== -1) {
                      const path = request.url.substring(index)

                      const newPath = path
                        .replace('candidates', 'applications')
                        .replace('feedback', 'notes')

                      return redirect(newPath)
                    }

                    return redirect(
                      navigation.ats.jobs.jobsId.applications.fullPath({
                        jobId: Number(params.jobId),
                      })
                    )
                  },
                },
              ],
              element: <JobDetails />,
            },

            // jobs universal importer
            {
              path: navigation.ats.jobs.importJobPostings.path,
              element: (
                <Routes>
                  <Route path="" element={<UniversalImporterJobPostingsPage />} />
                </Routes>
              ),
              handle: {
                resource: true,
                meta: () => ({
                  title: i18n.t('ats.routes.job_postings_universal_importer'),
                  headerComponent: 'auto',
                }),
              },
            },
          ],
        },
        // -- Candidates
        {
          path: navigation.ats.candidates.path,
          handle: {
            meta: () => ({
              title: i18n.t('ats.routes.candidates'),
            }),
          },

          children: [
            {
              index: true,
              element: <CandidatesPage />,
            },
            {
              path: navigation.ats.candidates.new.path,
              element: <NewCandidatePage />,
            },
            {
              path: navigation.ats.candidates.import.path,
              element: <ImportCandidatesPage />,
            },
            {
              path: navigation.ats.candidates.importCandidates.path,
              element: (
                <Routes>
                  <Route path="" element={<UniversalImporterCandidatesPage />} />
                </Routes>
              ),
              handle: {
                resource: true,
                meta: () => ({
                  title: i18n.t('ats.routes.candidates_universal_importer'),
                  headerComponent: (
                    <ResourceHeader title={i18n.t('ats.routes.candidates_universal_importer')} />
                  ),
                }),
              },
            },
            {
              path: navigation.ats.candidates.newEmployeeCandidate.path,
              element: <NewCandidateFromEmployeePage />,
            },
            // -- Candidate
            {
              path: navigation.ats.candidates.id.path,
              handle: {
                resource: true,
                meta: ({ watchQuery, params, session }) => {
                  if (!params.id) {
                    return { title: '' }
                  }

                  return watchQuery({
                    query: AtsCandidateHeaderDocument,
                    variables: {
                      id: Number(params.id),
                      companyId: session.company.id,
                    },
                  }).map(({ data }) => {
                    const candidate = data.ats.candidatesConnection.nodes[0]
                    const isDataConsentLimitEnabled =
                      data.ats.dataRetentionConfig.nodes[0]?.consentLimitEnabled ?? false
                    const isWhatsappFeatureEnabled =
                      data?.features.enabledFeatures.nodes.find(
                        (node) => node.name === 'ats_whatsapp'
                      )?.enabled ?? false
                    const companyInstallations =
                      data?.marketplace?.companyInstallationsConnection?.nodes || []
                    const isWhatsappInstalled = companyInstallations.some(
                      (installation) => installation.integrationDatoId === WHATSAPP_INTEGRATION_ID
                    )

                    if (!candidate) {
                      return { title: '' }
                    }

                    return {
                      title: candidate.fullName,
                      headerComponent: (
                        <CandidateHeader
                          candidateData={candidate}
                          isDataConsentLimitEnabled={isDataConsentLimitEnabled}
                          isWhatsappFeatureEnabled={isWhatsappFeatureEnabled}
                          isWhatsappInstalled={isWhatsappInstalled}
                        />
                      ),
                      subNavigation: [
                        {
                          title: i18n.t('ats.candidate_details.tabs.applications'),
                          href: navigation.ats.candidates.id.applications.fullPath({
                            id: candidate.id,
                          }),
                          index: true,
                        },
                        {
                          title: i18n.t('ats.candidate_details.tabs.cv'),
                          href: navigation.ats.candidates.id.cv.fullPath({ id: candidate.id }),
                        },
                        {
                          title: i18n.t('ats.candidate_details.tabs.notes'),
                          href: navigation.ats.candidates.id.notes.fullPath({ id: candidate.id }),
                        },
                        {
                          title: i18n.t('ats.candidate_details.tabs.files'),
                          href: navigation.ats.candidates.id.files.fullPath({ id: candidate.id }),
                        },
                      ],
                    }
                  })
                },
              },

              children: [
                {
                  index: true,
                  loader: ({ params }) =>
                    redirect(
                      navigation.ats.candidates.id.applications.fullPath({ id: Number(params.id) })
                    ),
                },
                {
                  path: navigation.ats.candidates.id.applications.path,
                  element: <CandidateApplications />,
                },
                {
                  path: navigation.ats.candidates.id.applications.path,
                  element: <CandidateApplications />,
                },
                {
                  path: navigation.ats.candidates.id.cv.path,
                  element: <CandidateCvPage />,
                },
                {
                  path: navigation.ats.candidates.id.files.path,
                  element: <CandidateFiles />,
                },
                {
                  path: navigation.ats.candidates.id.notes.path,
                  element: <CandidateNotesPage />,
                },
              ],
              element: <CandidateDetailPage />,
            },
          ],
        },
        {
          path: navigation.ats.insights.path,

          handle: {
            meta: () => ({
              title: i18n.t('ats.routes.insights'),
            }),
          },
          element: <InsightsPage />,
        },
        {
          path: navigation.ats.openPositions.path,

          handle: {
            meta: () => ({
              title: i18n.t('ats.routes.open_positions'),
            }),
          },
          element: <OpenPositionsPage />,
        },
        {
          path: navigation.ats.purchaseRequests.path,

          handle: {
            meta: () => ({
              title: i18n.t('ats.routes.purchase_requests'),
            }),
          },
          element: <PurchaseRequestsPage />,
          children: [
            {
              path: navigation.ats.purchaseRequests.detail.path,
              element: <PurchaseRequestDetailPage />,
            },
          ],
        },
        {
          path: navigation.ats.messages.path,

          handle: {
            meta: () => ({
              title: i18n.t('ats.routes.messages'),
            }),
          },

          children: [
            {
              index: true,
              element: <Overview />,
            },
            {
              path: navigation.ats.messages.conversation.path,
              element: <MessageInboxConversationViewPage />,
            },
          ],
          element: <MessagesInboxPage />,
        },
      ],
    }),
  ]
}
